import { useMemo } from 'react'

import { useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { loadCreatorsFx } from '@/models/creators.ts'
import type { Creator } from '@/modules/creators/types.ts'
import { useModifyLicenseContext } from '@/modules/licenses/components/modify-license-context.tsx'
import { useConnect } from '@/modules/licenses/hooks/use-connect-creator.ts'
import { handleError } from '@/utils/error'

import type { BillingPlan, License } from '../../types'

interface SetLicenseDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  plan?: BillingPlan | undefined
  license?: License | undefined
  creator?: Creator | undefined
}

export const SetLicenseDialog = (props: SetLicenseDialogProps) => {
  const { plan, license, open, setOpen, creator } = props
  const { plans, licenses } = useModifyLicenseContext()
  const connect = useConnect()
  const queryClient = useQueryClient()

  const totalLicenses = useMemo(
    () => {
      if (licenses && plan) {
        const targetLicenses = licenses.filter(
          (license) =>
            !license.disabled &&
            license.activeUsed < license.quantity &&
            license.plan.startsWith(plan.name),
        )

        if (targetLicenses.length > 0) {
          return targetLicenses
            .map((license) => license.quantity - license.activeUsed)
            .reduce((a, b) => a + b, 0)
        }
      }

      return 0
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [licenses, plans, plan],
  )

  const toast = useToast()

  const handleClose = () => setOpen(false)

  const handleSubmit = () => {
    if (!creator || !license) {
      return
    }
    connect
      .mutateAsync({ creatorId: creator.id, licenseId: license.id })
      .then(() => {
        toast.success(`${creator?.name} has been successfully licensed.`)
      })
      .catch((error) => {
        handleError(error, 'There was an error with licensing the creator.')
      })
      .finally(() => {
        setOpen(false)
        connect.reset()
        loadCreatorsFx()
        queryClient.invalidateQueries('licenses')
        queryClient.invalidateQueries('creator')
      })
  }

  if (!plan || !license || !creator) {
    return
  }

  return (
    <Dialog
      show={open}
      onClose={handleClose}
    >
      <Dialog.Header>
        <Dialog.Title>Switch License to {plan.displayName}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description>
        <p className="mt-2 text-center text-title">
          Do you want to use 1 of {totalLicenses} {plan.displayName} license(s)
          for <span className="text-title">{creator?.name}</span>?
        </p>
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={handleClose}>
        <Button
          onClick={handleSubmit}
          isLoading={connect.isLoading}
        >
          Switch
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
